import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Dialog,
  IconButton,
  AppBar,
  Toolbar,
  Slide,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MapCanvas from './map/MapCanvas';
import MapControls from './map/MapControls';
import MapVendorList from './map/MapVendorList';
import VendorCard from './map/VendorCard';
import MapLegend from './map/MapLegend';
import api from '../services/api';

const FAVORITES_STORAGE_KEY = 'gingham:favoriteBooths';
const SEARCH_DEBOUNCE_MS = 200;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const loadFavorites = () => {
  try {
    const raw = window.localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return new Set(parsed);
    return new Set();
  } catch {
    return new Set();
  }
};

const saveFavorites = (set) => {
  try {
    window.localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch {
    // Storage may be unavailable (private mode); favorites just don't persist.
  }
};

// Pull deep-link params off the URL once at mount so visitors who paste a
// shared link land on the right booth/filter without us writing a custom
// router integration.
const readUrlState = (search) => {
  const params = new URLSearchParams(search);
  const boothId = Number(params.get('booth'));
  const categoryParam = params.get('category') || '';
  const categories = categoryParam
    ? categoryParam.split(',').map((s) => s.trim()).filter(Boolean)
    : [];
  return {
    boothId: Number.isFinite(boothId) && boothId > 0 ? boothId : null,
    categories: new Set(categories),
  };
};

const MapView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const initialUrlState = useMemo(() => readUrlState(location.search), []); // eslint-disable-line react-hooks/exhaustive-deps

  const [cells, setCells] = useState([]);
  const [landmarks, setLandmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategorySlugs, setSelectedCategorySlugs] = useState(initialUrlState.categories);
  const [selectedBoothId, setSelectedBoothId] = useState(initialUrlState.boothId);
  const [highlightedBoothId, setHighlightedBoothId] = useState(null);
  const [vendorCardOpen, setVendorCardOpen] = useState(initialUrlState.boothId != null);
  const [view, setView] = useState('map');
  const [sortMode, setSortMode] = useState('alpha');
  const [favorites, setFavorites] = useState(() => loadFavorites());
  const [snackbar, setSnackbar] = useState(null);

  const transformRef = useRef(null);
  const highlightTimeoutRef = useRef(null);

  // Debounce search input so typing doesn't thrash the SVG. We keep the raw
  // input separate so the text field stays responsive.
  useEffect(() => {
    const handle = setTimeout(() => setSearchQuery(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Sync favorites to localStorage whenever the set changes.
  useEffect(() => {
    saveFavorites(favorites);
  }, [favorites]);

  // Sync booth + category state into the URL so refreshes and shared links
  // restore the same view.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (selectedBoothId) {
      params.set('booth', String(selectedBoothId));
    } else {
      params.delete('booth');
    }
    if (selectedCategorySlugs.size > 0) {
      params.set('category', Array.from(selectedCategorySlugs).join(','));
    } else {
      params.delete('category');
    }
    const next = `${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
    if (`${location.pathname}${location.search}` !== next) {
      navigate(next, { replace: true });
    }
  }, [selectedBoothId, selectedCategorySlugs, navigate, location.pathname, location.search]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        const [mapRes, landmarkRes] = await Promise.all([
          api.get('/map'),
          api.get('/map/landmarks').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setCells(mapRes.data || []);
        setLandmarks(landmarkRes.data || []);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || err.message || 'Failed to load map');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, []);

  // Once cells load, pull up the deep-linked booth (if any) so the card opens
  // pointing at the right vendor.
  useEffect(() => {
    if (cells.length === 0) return;
    if (!initialUrlState.boothId) return;
    const cell = cells.find((c) => c.id === initialUrlState.boothId);
    if (cell) {
      setVendorCardOpen(true);
      // Defer zoom until the SVG mounts.
      setTimeout(() => zoomToBooth(cell.id), 250);
    }
  }, [cells, initialUrlState.boothId]); // eslint-disable-line react-hooks/exhaustive-deps

  const { width, height } = useMemo(() => {
    let w = 0;
    let h = 0;
    for (const c of cells) {
      if (c.x_coordinate > w) w = c.x_coordinate;
      if (c.y_coordinate > h) h = c.y_coordinate;
    }
    return { width: w, height: h };
  }, [cells]);

  // Build the category list shown in chips and the legend from the cells we
  // actually loaded. Only categories with at least one represented vendor
  // appear, which keeps the chip row focused on what's at the festival.
  const categories = useMemo(() => {
    const map = new Map();
    for (const cell of cells) {
      if (cell.cell_type !== 'booth' || !cell.is_registered) continue;
      if (!cell.category_slug) continue;
      const existing = map.get(cell.category_slug);
      if (existing) {
        existing.count += 1;
      } else {
        map.set(cell.category_slug, {
          slug: cell.category_slug,
          name: cell.category_name || cell.category_slug,
          count: 1,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [cells]);

  const landmarkKinds = useMemo(() => {
    const seen = new Set();
    for (const l of landmarks) seen.add(l.kind);
    return Array.from(seen);
  }, [landmarks]);

  const favoriteBoothIds = favorites;

  const zoomToBooth = useCallback((boothId) => {
    if (!transformRef.current) return;
    const target = `booth-${boothId}`;
    try {
      transformRef.current.zoomToElement(target, 1.6, 350);
    } catch {
      // zoomToElement throws when the node isn't mounted yet; safe to ignore.
    }
    setHighlightedBoothId(boothId);
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    highlightTimeoutRef.current = setTimeout(() => setHighlightedBoothId(null), 2200);
  }, []);

  const handleClose = () => {
    navigate(-1);
  };

  const handleBoothClick = (cell) => {
    setSelectedBoothId(cell.id);
    setVendorCardOpen(true);
  };

  const handleSelectFromList = (cell) => {
    setSelectedBoothId(cell.id);
    setVendorCardOpen(true);
    zoomToBooth(cell.id);
  };

  const handleSearchSubmit = () => {
    const q = (searchInput || '').trim().toLowerCase();
    if (!q) return;
    const match = cells.find((c) => {
      if (c.cell_type !== 'booth' || !c.is_registered) return false;
      const haystack = [c.vendor_name, c.category_name, c.cell_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
    if (match) {
      handleSelectFromList(match);
    } else {
      setSnackbar({ severity: 'info', message: 'No vendor matched that search.' });
    }
  };

  const handleToggleCategory = (slug) => {
    setSelectedCategorySlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const handleClearCategories = () => setSelectedCategorySlugs(new Set());

  const handleZoomIn = () => transformRef.current?.zoomIn(0.25);
  const handleZoomOut = () => transformRef.current?.zoomOut(0.25);
  const handleZoomReset = () => transformRef.current?.resetTransform();

  const handleToggleFavorite = (boothId) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(boothId)) next.delete(boothId);
      else next.add(boothId);
      return next;
    });
  };

  const handleShareBooth = async () => {
    if (!selectedBoothId) return;
    const shareUrl = `${window.location.origin}/map?booth=${selectedBoothId}`;
    const shareTitle = selectedCell?.vendor_name
      ? `${selectedCell.vendor_name} at Gingham Tree`
      : 'Gingham Tree booth';
    try {
      if (navigator.share) {
        await navigator.share({ title: shareTitle, url: shareUrl });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        setSnackbar({ severity: 'success', message: 'Booth link copied to clipboard' });
      }
    } catch {
      // User cancelled or share failed; nothing to do.
    }
  };

  const selectedCell = useMemo(
    () => (selectedBoothId ? cells.find((c) => c.id === selectedBoothId) : null),
    [cells, selectedBoothId]
  );

  const showList = view === 'split' && !isMobile;

  return (
    <Dialog fullScreen open onClose={handleClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: 'relative', background: '#fff', color: '#222', boxShadow: 1 }}>
        <Toolbar sx={{ minHeight: 56 }}>
          <Typography sx={{ flex: 1 }} variant="h6" component="div" fontWeight={700}>
            Festival Map
          </Typography>
          <IconButton edge="end" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <MapControls
        searchQuery={searchInput}
        onSearchChange={setSearchInput}
        onSearchSubmit={handleSearchSubmit}
        categories={categories}
        selectedCategorySlugs={selectedCategorySlugs}
        onToggleCategory={handleToggleCategory}
        onClearCategories={handleClearCategories}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onZoomReset={handleZoomReset}
        view={view}
        onViewChange={setView}
      />

      <Box sx={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative', background: '#f5f6fa' }}>
        {loading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : width === 0 || height === 0 ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Alert severity="info">
              The map for the active festival hasn't been published yet. Check back soon!
            </Alert>
          </Box>
        ) : (
          <>
            <Box sx={{ flex: 1, position: 'relative', minWidth: 0 }}>
              <MapCanvas
                cells={cells}
                landmarks={landmarks}
                width={width}
                height={height}
                searchQuery={searchQuery}
                selectedCategorySlugs={selectedCategorySlugs}
                selectedBoothId={selectedBoothId}
                highlightedBoothId={highlightedBoothId}
                favoriteBoothIds={favoriteBoothIds}
                onBoothClick={handleBoothClick}
                transformRef={transformRef}
              />
              <MapLegend categories={categories} landmarkKinds={landmarkKinds} />
            </Box>
            {showList && (
              <Box sx={{ width: 320, flexShrink: 0 }}>
                <MapVendorList
                  cells={cells}
                  searchQuery={searchQuery}
                  selectedCategorySlugs={selectedCategorySlugs}
                  selectedBoothId={selectedBoothId}
                  favoriteBoothIds={favoriteBoothIds}
                  onSelectBooth={handleSelectFromList}
                  onToggleFavorite={handleToggleFavorite}
                  sortMode={sortMode}
                  onSortModeChange={setSortMode}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      <VendorCard
        cell={selectedCell}
        open={vendorCardOpen && !!selectedCell}
        onClose={() => setVendorCardOpen(false)}
        onShowOnMap={() => selectedCell && zoomToBooth(selectedCell.id)}
        isFavorite={selectedCell ? favorites.has(selectedCell.id) : false}
        onToggleFavorite={() => selectedCell && handleToggleFavorite(selectedCell.id)}
        onShare={handleShareBooth}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3500}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} variant="filled">
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Dialog>
  );
};

export default MapView;
